import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getExchangeRun, type ExchangeRunDetail } from "@/lib/exchange-trace.functions";
import { PageHeader } from "@/components/ui/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft, CheckCircle2, XCircle, MinusCircle, AlertTriangle, Loader2,
} from "lucide-react";

/**
 * Operator Trace — "Why did this happen?" view.
 *
 * Read-only by design. No replay, no edit, no retry. The page reads a single
 * `exchange_ingest_runs` row and presents:
 *   • header (envelope id, source, entity, mode, outcome, timing)
 *   • mapping profile + version pinned at ingest time
 *   • stage timeline (mapping → transform → alias → validation → policy →
 *     persist → audit) with per-stage status, details, and errors
 *   • applied policy decisions (declarative intent only)
 *   • quarantine reasons (unresolved aliases)
 *   • warnings + errors
 *   • the raw envelope as the source delivered it
 *   • the canonical object the pipeline produced
 *
 * Architectural note: `applied_changes` / policy decisions remain a small
 * declarative vocabulary (e.g. set_hold, add_tags, set_priority). This view
 * renders them as data, never as executable rules.
 */
export const Route = createFileRoute("/_authenticated/app/admin/exchange-runs/$runId")({
  component: ExchangeRunDetailPage,
});

const STAGE_ORDER = ["mapping", "transform", "alias", "validation", "policy", "persist", "audit"] as const;
const STAGE_LABEL: Record<(typeof STAGE_ORDER)[number], string> = {
  mapping: "Mapping",
  transform: "Transform",
  alias: "Alias resolution",
  validation: "Validation",
  policy: "Policy",
  persist: "Persistence",
  audit: "Audit / Trace",
};

function ExchangeRunDetailPage() {
  const { runId } = useParams({ from: "/_authenticated/app/admin/exchange-runs/$runId" });
  const get = useServerFn(getExchangeRun);
  const { data, isLoading, error } = useQuery({
    queryKey: ["exchange-run", runId],
    queryFn: () => get({ data: { runId } }),
  });

  if (isLoading) {
    return (
      <div className="py-20 flex justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="px-6 py-10 text-sm text-destructive">
        Trace not found or access denied.
      </div>
    );
  }

  const { run, profile, profile_version } = data;
  const outcomeBadge = run.unresolved_aliases?.length
    ? <Badge variant="outline" className="bg-warning/10 text-warning border-warning/30">Quarantined</Badge>
    : run.ok
      ? <Badge variant="outline" className="bg-success/10 text-success border-success/30">OK</Badge>
      : <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive/30">Failed</Badge>;

  const policyStage: any = run.stages?.policy ?? null;
  const policyDecisions: any[] = Array.isArray(policyStage?.details?.decisions)
    ? policyStage.details.decisions
    : Array.isArray((policyStage as any)?.decisions)
      ? (policyStage as any).decisions
      : [];

  return (
    <>
      <PageHeader
        title="Exchange Trace"
        description={`Envelope ${run.envelope_id}`}
        actions={
          <Button asChild variant="ghost" size="sm">
            <Link to="/app/admin/exchange-runs">
              <ArrowLeft className="h-4 w-4 mr-1" /> All traces
            </Link>
          </Button>
        }
      />
      <div className="px-4 sm:px-6 py-4 space-y-6 max-w-5xl">
        {/* Header summary */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4 border rounded-md p-4 bg-card">
          <Field label="Outcome">{outcomeBadge}</Field>
          <Field label="Mode"><Badge variant="outline">{run.mode}</Badge></Field>
          <Field label="Entity"><span className="font-mono text-sm">{run.entity}</span></Field>
          <Field label="Direction"><span className="font-mono text-sm">{run.direction}</span></Field>
          <Field label="Source">
            <div className="text-sm">
              <div className="font-medium">{run.source_kind}</div>
              {run.source_namespace && (
                <div className="text-xs text-muted-foreground">{run.source_namespace}</div>
              )}
            </div>
          </Field>
          <Field label="External record id">
            <span className="font-mono text-xs">{run.source_record_id ?? "—"}</span>
          </Field>
          <Field label="Persisted id">
            <span className="font-mono text-xs">{run.persisted_id ?? "—"}</span>
          </Field>
          <Field label="Connection">
            <span className="font-mono text-xs">{run.connection_id ?? "—"}</span>
          </Field>
          <Field label="Idempotency key" full>
            <span className="font-mono text-xs break-all">{run.idempotency_key ?? "—"}</span>
          </Field>
          <Field label="Started">{run.started_at ? new Date(run.started_at).toLocaleString() : "—"}</Field>
          <Field label="Completed">{run.completed_at ? new Date(run.completed_at).toLocaleString() : "—"}</Field>
          <Field label="Duration">
            {run.started_at && run.completed_at
              ? `${Math.max(0, new Date(run.completed_at).getTime() - new Date(run.started_at).getTime())} ms`
              : "—"}
          </Field>
          <Field label="Run id" full>
            <span className="font-mono text-xs break-all">{run.id}</span>
          </Field>
        </section>

        {/* Mapping profile / version */}
        <section className="border rounded-md p-4 bg-card">
          <SectionHeader title="Mapping Profile (pinned at Ingest)" />
          {profile || profile_version ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <Field label="Profile">{profile?.name ?? <span className="text-muted-foreground">identity fallback</span>}</Field>
              <Field label="Entity"><span className="font-mono text-xs">{profile?.entity ?? "—"}</span></Field>
              <Field label="Source kind"><span className="font-mono text-xs">{profile?.source_kind ?? "—"}</span></Field>
              <Field label="Status">{profile?.status ?? "—"}</Field>
              <Field label="Version #">
                {profile_version?.version_number != null ? `v${profile_version.version_number}` : "—"}
              </Field>
              <Field label="Published at">
                {profile_version?.published_at
                  ? new Date(profile_version.published_at).toLocaleString()
                  : "—"}
              </Field>
              <Field label="Change notes" full>
                <span className="text-sm">{profile_version?.change_notes || "—"}</span>
              </Field>
              <Field label="Profile id"><span className="font-mono text-xs">{run.profile_id ?? "—"}</span></Field>
              <Field label="Profile version id" full>
                <span className="font-mono text-xs break-all">{run.profile_version_id ?? "—"}</span>
              </Field>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              No mapping profile was resolved — the pipeline ran with the identity fallback.
            </p>
          )}
        </section>

        {/* Stage timeline */}
        <section className="border rounded-md p-4 bg-card">
          <SectionHeader title="Stage Timeline" />
          <ol className="space-y-3">
            {STAGE_ORDER.map((name) => {
              const stage: any = (run.stages as any)?.[name];
              return <StageRow key={name} name={name} stage={stage} />;
            })}
          </ol>
        </section>

        {/* Policy decisions */}
        <section className="border rounded-md p-4 bg-card">
          <SectionHeader
            title="Applied Policy Decisions"
            hint="Declarative intent — interpreted by the entity adapter at persist time."
          />
          {policyDecisions.length === 0 ? (
            <p className="text-sm text-muted-foreground">No policy decisions for this envelope.</p>
          ) : (
            <ul className="space-y-2">
              {policyDecisions.map((d, i) => (
                <li key={i} className="border rounded p-2 text-xs bg-muted/30">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <Badge variant="outline" className="font-mono">{String(d.action ?? d.change ?? d.kind ?? "decision")}</Badge>
                    {d.evaluator_id && (
                      <span className="text-muted-foreground">from <span className="font-mono">{d.evaluator_id}</span></span>
                    )}
                    {d.reason && <span className="text-muted-foreground">— {String(d.reason)}</span>}
                  </div>
                  <pre className="text-[11px] whitespace-pre-wrap overflow-auto">{JSON.stringify(d, null, 2)}</pre>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Quarantine */}
        <section className="border rounded-md p-4 bg-card">
          <SectionHeader title="Quarantine — Unresolved Aliases" />
          {(!run.unresolved_aliases || run.unresolved_aliases.length === 0) ? (
            <p className="text-sm text-muted-foreground">No unresolved aliases.</p>
          ) : (
            <div className="space-y-2">
              {(run.unresolved_aliases as any[]).map((u, i) => (
                <div key={i} className="text-xs border rounded p-2 flex flex-wrap items-center gap-2 bg-warning/5">
                  <Badge variant="outline" className="bg-warning/10 text-warning border-warning/30">
                    {String(u.entity ?? "alias")}
                  </Badge>
                  <span className="font-mono">{String(u.source ?? "")}{u.namespace ? `:${u.namespace}` : ""}</span>
                  <span className="text-muted-foreground">→</span>
                  <span className="font-mono">{String(u.external_id ?? "")}</span>
                  {u.quarantine_id && (
                    <span className="ml-auto text-muted-foreground font-mono">
                      quarantine_id: {String(u.quarantine_id)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Warnings & errors */}
        <section className="grid md:grid-cols-2 gap-4">
          <div className="border rounded-md p-4 bg-card">
            <SectionHeader title={`Warnings (${run.warnings?.length ?? 0})`} />
            {(!run.warnings || run.warnings.length === 0) ? (
              <p className="text-sm text-muted-foreground">No warnings.</p>
            ) : (
              <ul className="space-y-1 text-xs">
                {(run.warnings as any[]).map((w, i) => (
                  <li key={i} className="font-mono">{typeof w === "string" ? w : JSON.stringify(w)}</li>
                ))}
              </ul>
            )}
          </div>
          <div className="border rounded-md p-4 bg-card">
            <SectionHeader title={`Errors (${run.errors?.length ?? 0})`} />
            {(!run.errors || run.errors.length === 0) ? (
              <p className="text-sm text-muted-foreground">No errors.</p>
            ) : (
              <ul className="space-y-2 text-xs">
                {(run.errors as any[]).map((e, i) => (
                  <li key={i} className="border rounded p-2 bg-destructive/5">
                    <pre className="whitespace-pre-wrap">{JSON.stringify(e, null, 2)}</pre>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        {/* Raw envelope + canonical */}
        <section className="grid md:grid-cols-2 gap-4">
          <JsonBlock title="Raw Incoming Envelope" data={run.raw_payload} />
          <JsonBlock title="Canonical Object" data={run.canonical} />
        </section>
      </div>
    </>
  );
}

function Field({ label, children, full }: { label: string; children: React.ReactNode; full?: boolean }) {
  return (
    <div className={full ? "col-span-2 md:col-span-4" : ""}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-sm">{children}</div>
    </div>
  );
}

function SectionHeader({ title, hint }: { title: string; hint?: string }) {
  return (
    <header className="mb-3">
      <h2 className="text-sm font-semibold">{title}</h2>
      {hint && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}
    </header>
  );
}

function StageRow({ name, stage }: { name: keyof typeof STAGE_LABEL; stage: any }) {
  const status: "ok" | "fail" | "skipped" | "missing" =
    !stage ? "missing"
    : stage.ok === true ? "ok"
    : stage.ok === false ? "fail"
    : stage.ok === null ? "skipped"
    : "missing";

  const Icon = status === "ok" ? CheckCircle2
    : status === "fail" ? XCircle
    : status === "skipped" ? MinusCircle
    : AlertTriangle;
  const iconClass = status === "ok" ? "text-success"
    : status === "fail" ? "text-destructive"
    : status === "skipped" ? "text-muted-foreground"
    : "text-warning";

  return (
    <li className="flex items-start gap-3">
      <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${iconClass}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{STAGE_LABEL[name]}</span>
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{status}</span>
          {status === "skipped" && stage?.skipped_because && (
            <span className="text-xs text-muted-foreground">
              (after <span className="font-mono">{String(stage.skipped_because)}</span>)
            </span>
          )}
        </div>
        {status === "ok" && stage?.details && Object.keys(stage.details).length > 0 && (
          <details className="mt-1">
            <summary className="text-xs text-muted-foreground cursor-pointer">details</summary>
            <pre className="text-[11px] mt-1 bg-muted rounded p-2 max-h-60 overflow-auto whitespace-pre-wrap">
              {JSON.stringify(stage.details, null, 2)}
            </pre>
          </details>
        )}
        {status === "fail" && Array.isArray(stage?.errors) && (
          <ul className="mt-1 space-y-1">
            {stage.errors.map((e: any, i: number) => (
              <li key={i} className="text-xs">
                <span className="font-mono text-destructive">{String(e.code ?? "error")}</span>
                {Array.isArray(e.path) && e.path.length > 0 && (
                  <span className="text-muted-foreground"> at <span className="font-mono">{e.path.join(".")}</span></span>
                )}
                <span>: {String(e.message ?? "")}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </li>
  );
}

function JsonBlock({ title, data }: { title: string; data: unknown }) {
  return (
    <div className="border rounded-md p-4 bg-card">
      <SectionHeader title={title} />
      <pre className="text-[11px] bg-muted rounded p-2 max-h-96 overflow-auto whitespace-pre-wrap">
        {data == null ? "null" : JSON.stringify(data, null, 2)}
      </pre>
    </div>
  );
}

// keep TS satisfied if unused-export checks fire on type-only re-export
export type { ExchangeRunDetail };
